import {
    AUTH_HEADERS,
    getAccessToken,
    getAnonymousIdToken,
    getCartIdToken,
    getChannel,
    getVisitorId,
    HttpMethod,
    HttpStatus,
    isAccessTokenAuth0,
} from '@libs/core';

import { cweaCartsClient } from '../clients/api-proxy-browser-client';
import { appendCartDelInstrQuery, getCartsPathPrefix } from '../utils';
import { buildSourceLocationHeader } from '../utils/source-location';

type CartFetchOptions<TBody> = {
    method: HttpMethod;
    pathname: string;
    body?: TBody;
    headers?: HeadersInit;
    additionalSuccessCodes?: number[];
    expectedSuccessStatus?: HttpStatus;
};

// Match the existing client-side cart fetch wrapper defaults (`libs/common-next/src/utils/fetchCart.ts`):
// treat 303 and 400 as successful so UI can read and display error details.
const defaultAdditionalSuccessCodes = [303, 400];

function withStatusIfObject<T>(
    result: T,
    httpStatus: number | undefined,
    expectedSuccessStatus: HttpStatus | undefined
): T | (T & { status: number }) {
    if (!result || typeof result !== 'object') return result;
    if (Array.isArray(result)) return result;

    const asRecord = result as Record<string, unknown>;
    const hasStatus = typeof asRecord.status === 'number';

    if (hasStatus) return result;

    return {
        ...(result as object),
        status: httpStatus ?? expectedSuccessStatus ?? HttpStatus.OK,
    } as T & { status: number };
}

function buildCartAuthHeaders(): HeadersInit {
    const accessToken = getAccessToken();
    const anonymousKey = getAnonymousIdToken();
    const visitorId = getVisitorId();
    const sendAuth0Header = Boolean(accessToken) && isAccessTokenAuth0();

    return {
        ...buildSourceLocationHeader(),
        ...(accessToken && { [AUTH_HEADERS.AUTHORIZATION]: `Bearer ${accessToken}` }),
        ...(sendAuth0Header && { [AUTH_HEADERS.X_PETM_AUTH0_ENABLED]: 'true' }),
        ...(anonymousKey && !accessToken && { [AUTH_HEADERS.X_PETM_ANONYMOUS_KEY]: anonymousKey }),
        ...(visitorId && { [AUTH_HEADERS.X_PETM_VISITOR_ID]: visitorId }),
    };
}

/**
 * Fetch against CWEA carts endpoints, mirroring the former Next.js route behavior:
 * - prefix path with `v2/` and/or `soft/` based on decisions + soft token cookie
 * - treat common cart status codes as "successful" so UI can render error details
 * - inject `status` into object responses for parity with existing callers
 */
export async function cweaCartsFetch<T, TBody = unknown>({
    method,
    pathname,
    body,
    headers,
    additionalSuccessCodes = defaultAdditionalSuccessCodes,
    expectedSuccessStatus,
}: CartFetchOptions<TBody>): Promise<T | null> {
    const prefix = getCartsPathPrefix();
    const normalizedPathname = pathname.startsWith('/') ? pathname.slice(1) : pathname;
    const fullPath = appendCartDelInstrQuery(`${prefix}${normalizedPathname}`);

    const cartIdToken = getCartIdToken();
    const channelValue = getChannel();
    const cartHeaders: HeadersInit = {
        ...buildCartAuthHeaders(),
        ...(cartIdToken && { [AUTH_HEADERS.X_PETM_CART_ID]: cartIdToken }),
        ...(channelValue && { [AUTH_HEADERS.X_PETM_CHANNEL]: channelValue }),
        ...headers,
    };

    const result = await cweaCartsClient.request<T, TBody>(method, fullPath, {
        body: body as TBody,
        headers: cartHeaders,
        additionalSuccessCodes,
        preserveHttpStatusForAdditionalSuccessCodes: true,
    });

    if (!result) return null;

    if (result instanceof Error) {
        return withStatusIfObject({ error: result.message } as T, HttpStatus.INTERNAL_SERVER_ERROR, undefined) as T;
    }

    if (result instanceof Response) {
        const parsed = (result as unknown as { parsedResponse?: T }).parsedResponse;
        if (parsed !== undefined) {
            return withStatusIfObject(parsed, result.status, expectedSuccessStatus) as T;
        }
        return withStatusIfObject({} as T, result.status, undefined) as T;
    }

    return withStatusIfObject(result, undefined, expectedSuccessStatus) as T;
}
